import React, { useState, useEffect } from 'react';
import { X, FileText, Check, Clock, Plus, Trash2, Pencil, LayoutList, Send } from 'lucide-react';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import { composeSectionsText } from '../../utils/meetingDisplay';

const MANAGER_ROLES = ['admin', 'manager', 'center_manager', 'super_admin'];

const newSectionRow = () => ({
  id: `sec_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  header: '',
  content: '',
});

export default function AddNoteBottomSheet({
  isOpen, onClose, meetings = [], preSelectedMeeting, onSuccess,
  isMobile, isRTL, t, onSendWhatsApp,
}) {
  const { user } = useAuthStore();
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const [tab, setTab] = useState('free');
  const [templates, setTemplates] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [sections, setSections] = useState([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorId, setEditorId] = useState(null);
  const [editorName, setEditorName] = useState('');
  const [editorShared, setEditorShared] = useState(false);
  const [editorSections, setEditorSections] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(preSelectedMeeting || null);
      setNote(preSelectedMeeting?.content || '');
      setEditorOpen(false);
      if (preSelectedMeeting?.noteTemplateId && Array.isArray(preSelectedMeeting?.noteSections) && preSelectedMeeting.noteSections.length > 0) {
        setTab('template');
        setSelectedTemplateId(preSelectedMeeting.noteTemplateId);
        setSections(preSelectedMeeting.noteSections.map((s, i) => ({
          ...s,
          id: (typeof s.id === 'string' && s.id) ? s.id : `sec_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 8)}`,
          checked: true,
        })));
      } else {
        setTab('free');
        setSelectedTemplateId(null);
        setSections([]);
      }
    }
  }, [isOpen, preSelectedMeeting]);

  useEffect(() => {
    if (!isOpen || tab !== 'template' || templates !== null) return;
    let cancelled = false;
    setLoadingTemplates(true);
    setTemplatesError(false);
    api.get('/note-templates')
      .then((res) => { if (!cancelled) setTemplates(Array.isArray(res) ? res : []); })
      .catch((err) => {
        console.error('Failed to load note templates:', err);
        if (!cancelled) setTemplatesError(true);
      })
      .finally(() => { if (!cancelled) setLoadingTemplates(false); });
    return () => { cancelled = true; };
  }, [isOpen, tab, templates]);

  if (!isOpen) return null;

  const canEditTemplate = (tpl) =>
    tpl.createdBy === user?.id || MANAGER_ROLES.includes(user?.role);

  const applyTemplate = (tpl) => {
    setSelectedTemplateId(tpl.id);
    setSections((tpl.sections || []).map((s) => ({ id: s.id, header: s.header, content: s.content, checked: true })));
  };

  const checkedSections = sections.filter((s) => s.checked && (s.header.trim() || s.content.trim()));
  const composedText = composeSectionsText(checkedSections);
  const effectiveText = tab === 'free' ? note.trim() : composedText;
  const canSave = !!selected && !!effectiveText && !saving;
  const canSendWhatsApp = !!effectiveText;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = tab === 'template'
        ? {
            content: composedText,
            noteTemplateId: selectedTemplateId,
            noteSections: checkedSections.map((s) => ({ id: s.id, header: s.header.trim(), content: s.content.trim() })),
          }
        : { content: note.trim(), noteTemplateId: null, noteSections: [] };
      await api.put(`/meetings/${selected.id}`, payload);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to save note:', err);
    } finally {
      setSaving(false);
    }
  };

  const openEditor = (tpl) => {
    if (tpl) {
      setEditorId(tpl.id);
      setEditorName(tpl.name || '');
      setEditorShared(tpl.scope === 'center');
      setEditorSections((tpl.sections || []).map((s) => ({ ...s })));
    } else {
      setEditorId(null);
      setEditorName('');
      setEditorShared(false);
      setEditorSections([newSectionRow()]);
    }
    setEditorOpen(true);
  };

  const editorValid = editorName.trim()
    && editorSections.length > 0
    && editorSections.every((s) => s.header.trim() || s.content.trim());

  const handleSaveTemplate = async () => {
    if (!editorValid || savingTemplate) return;
    setSavingTemplate(true);
    try {
      const body = {
        name: editorName.trim(),
        sections: editorSections.map((s) => ({ id: s.id, header: s.header.trim(), content: s.content.trim() })),
        scope: editorShared ? 'center' : 'personal',
      };
      const saved = editorId
        ? await api.put(`/note-templates/${editorId}`, body)
        : await api.post('/note-templates', body);
      const tpl = saved?.id ? saved : { id: editorId, ...body };
      setTemplates((prev) => {
        const list = Array.isArray(prev) ? prev.filter((x) => x.id !== tpl.id) : [];
        return [...list, tpl].sort((a, b) => String(a.name).localeCompare(String(b.name)));
      });
      applyTemplate(tpl);
      setEditorOpen(false);
    } catch (err) {
      console.error('Failed to save note template:', err);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!editorId || savingTemplate) return;
    if (!window.confirm(t('noteTemplates.deleteConfirm', 'Delete this template?'))) return;
    setSavingTemplate(true);
    try {
      await api.delete(`/note-templates/${editorId}`);
      setTemplates((prev) => (Array.isArray(prev) ? prev.filter((x) => x.id !== editorId) : prev));
      if (selectedTemplateId === editorId) {
        setSelectedTemplateId(null);
        setSections([]);
      }
      setEditorOpen(false);
    } catch (err) {
      console.error('Failed to delete note template:', err);
    } finally {
      setSavingTemplate(false);
    }
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, zIndex: 9999,
    backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: isMobile ? 'flex-end' : 'center',
    justifyContent: 'center',
    direction: isRTL ? 'rtl' : 'ltr',
  };

  const sheetStyle = {
    backgroundColor: 'var(--color-surface)',
    borderRadius: isMobile ? '16px 16px 0 0' : '12px',
    padding: '1.25rem',
    width: isMobile ? '100%' : '460px',
    maxHeight: isMobile ? '85vh' : '80vh',
    overflow: 'auto',
    animation: isMobile ? 'sheetSlideUp 0.25s ease' : 'sheetFadeIn 0.2s ease',
  };

  const inputStyle = {
    width: '100%', padding: '0.5rem 0.625rem', borderRadius: '8px',
    border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)',
    fontSize: '0.8125rem', fontFamily: 'inherit',
    color: 'var(--color-text-primary)',
    direction: isRTL ? 'rtl' : 'ltr',
    boxSizing: 'border-box',
  };

  const tabButtonStyle = (active) => ({
    flex: 1, padding: '0.5rem', borderRadius: '8px', border: 'none',
    backgroundColor: active ? 'var(--color-surface)' : 'transparent',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
    color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
    fontWeight: active ? 600 : 500, fontSize: '0.8125rem', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
    minHeight: '38px',
  });

  const smallButtonStyle = {
    padding: '0.375rem 0.625rem', borderRadius: '8px',
    border: '1px dashed var(--color-border)', backgroundColor: 'transparent',
    color: 'var(--color-primary)', fontWeight: 600, fontSize: '0.75rem',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem',
  };

  const renderTemplateEditor = () => (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', padding: '0.75rem', marginBottom: '0.75rem' }}>
      <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '0.5rem' }}>
        {editorId ? t('noteTemplates.editTemplate', 'Edit template') : t('noteTemplates.createTemplate', 'New template')}
      </div>
      <input
        value={editorName}
        onChange={(e) => setEditorName(e.target.value)}
        placeholder={t('noteTemplates.templateNamePlaceholder', 'Template name...')}
        style={{ ...inputStyle, marginBottom: '0.5rem', fontWeight: 600 }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {editorSections.map((s, i) => (
          <div key={s.id} style={{ border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
              <input
                value={s.header}
                onChange={(e) => setEditorSections((prev) => prev.map((x) => (x.id === s.id ? { ...x, header: e.target.value } : x)))}
                placeholder={t('noteTemplates.sectionHeaderPlaceholder', 'Section header...')}
                style={{ ...inputStyle, fontWeight: 600 }}
              />
              <button
                onClick={() => setEditorSections((prev) => prev.filter((x) => x.id !== s.id))}
                disabled={editorSections.length <= 1}
                title={t('noteTemplates.removeSection', 'Remove section')}
                style={{
                  width: '30px', height: '30px', borderRadius: '8px', border: 'none', flexShrink: 0,
                  backgroundColor: 'var(--color-surface-alt, #F1F4F8)',
                  color: editorSections.length <= 1 ? 'var(--color-border)' : 'var(--color-danger, #C0392B)',
                  cursor: editorSections.length <= 1 ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <Trash2 size={13} style={{ flexShrink: 0, width: 13, height: 13 }} />
              </button>
            </div>
            <textarea
              value={s.content}
              onChange={(e) => setEditorSections((prev) => prev.map((x) => (x.id === s.id ? { ...x, content: e.target.value } : x)))}
              placeholder={t('noteTemplates.sectionContentPlaceholder', 'Section content...')}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
        <button onClick={() => setEditorSections((prev) => [...prev, newSectionRow()])} style={smallButtonStyle}>
          <Plus size={13} style={{ flexShrink: 0, width: 13, height: 13 }} />
          {t('noteTemplates.addSection', 'Add section')}
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.75rem', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={editorShared}
            onChange={(e) => setEditorShared(e.target.checked)}
            style={{ accentColor: 'var(--color-primary)', width: 15, height: 15 }}
          />
          {t('noteTemplates.shareWithCenter', 'Share with the whole center')}
        </label>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        <button
          onClick={handleSaveTemplate}
          disabled={!editorValid || savingTemplate}
          style={{
            flex: 1, padding: '0.5rem', borderRadius: '8px', border: 'none',
            backgroundColor: !editorValid ? 'var(--color-border)' : 'var(--color-primary)',
            color: editorValid ? 'var(--color-on-primary, #fff)' : 'white', fontWeight: 600, fontSize: '0.8125rem',
            cursor: !editorValid ? 'default' : 'pointer', minHeight: '38px',
          }}>
          {savingTemplate ? t('common.saving', 'Saving...') : t('noteTemplates.saveTemplate', 'Save template')}
        </button>
        {editorId && (
          <button
            onClick={handleDeleteTemplate}
            disabled={savingTemplate}
            style={{
              padding: '0.5rem 0.75rem', borderRadius: '8px',
              border: '1px solid var(--color-danger, #C0392B)', backgroundColor: 'transparent',
              color: 'var(--color-danger, #C0392B)', fontWeight: 600, fontSize: '0.8125rem',
              cursor: 'pointer', minHeight: '38px',
            }}>
            {t('common.delete', 'Delete')}
          </button>
        )}
        <button
          onClick={() => setEditorOpen(false)}
          style={{
            padding: '0.5rem 0.75rem', borderRadius: '8px',
            border: '1px solid var(--color-border)', backgroundColor: 'transparent',
            color: 'var(--color-text-secondary)', fontWeight: 600, fontSize: '0.8125rem',
            cursor: 'pointer', minHeight: '38px',
          }}>
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </div>
  );

  const renderTemplateTab = () => {
    if (editorOpen) return renderTemplateEditor();
    return (
      <div style={{ marginBottom: '0.25rem' }}>
        {loadingTemplates && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', padding: '0.75rem 0' }}>
            {t('noteTemplates.loading', 'Loading templates...')}
          </div>
        )}
        {templatesError && !loadingTemplates && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-danger, #C0392B)', padding: '0.75rem 0' }}>
            {t('noteTemplates.loadError', 'Failed to load templates')}
          </div>
        )}
        {!loadingTemplates && !templatesError && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
                {t('noteTemplates.selectTemplate', 'Choose a template')}
              </div>
              <button onClick={() => openEditor(null)} style={smallButtonStyle}>
                <Plus size={13} style={{ flexShrink: 0, width: 13, height: 13 }} />
                {t('noteTemplates.createTemplate', 'New template')}
              </button>
            </div>
            {(templates || []).length === 0 ? (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-tertiary)', padding: '0.5rem 0 0.75rem' }}>
                {t('noteTemplates.noTemplates', 'No templates yet — create your first one')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', maxHeight: '150px', overflow: 'auto', marginBottom: '0.75rem' }}>
                {(templates || []).map((tpl) => (
                  <div key={tpl.id}
                    onClick={() => applyTemplate(tpl)}
                    style={{
                      padding: '0.5rem 0.625rem', borderRadius: '8px', cursor: 'pointer',
                      border: `1.5px solid ${selectedTemplateId === tpl.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                      backgroundColor: selectedTemplateId === tpl.id ? 'rgba(53,87,96,0.05)' : 'transparent',
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                    }}>
                    <LayoutList size={13} color="var(--color-text-tertiary)" style={{ flexShrink: 0, width: 13, height: 13 }} />
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-primary)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tpl.name}
                    </span>
                    {tpl.scope === 'center' && (
                      <span style={{
                        fontSize: '0.625rem', color: 'var(--color-text-tertiary)',
                        padding: '0.125rem 0.375rem', borderRadius: 999,
                        background: 'var(--color-surface-alt, #F1F4F8)', flexShrink: 0,
                      }}>
                        {t('noteTemplates.sharedBadge', 'Shared')}
                      </span>
                    )}
                    {canEditTemplate(tpl) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditor(tpl); }}
                        title={t('noteTemplates.editTemplate', 'Edit template')}
                        style={{
                          width: '26px', height: '26px', borderRadius: '6px', border: 'none', flexShrink: 0,
                          backgroundColor: 'var(--color-surface-alt, #F1F4F8)', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'var(--color-text-secondary)',
                        }}>
                        <Pencil size={12} style={{ flexShrink: 0, width: 12, height: 12 }} />
                      </button>
                    )}
                    {selectedTemplateId === tpl.id && <Check size={14} color="var(--color-primary)" style={{ flexShrink: 0 }} />}
                  </div>
                ))}
              </div>
            )}
            {selectedTemplateId && sections.length > 0 && (
              <>
                <div style={{ fontSize: '0.6875rem', color: 'var(--color-text-tertiary)', marginBottom: '0.375rem' }}>
                  {t('noteTemplates.sectionsHint', 'Unchecked sections will not be included')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {sections.map((s) => (
                    <div key={s.id} style={{
                      border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.5rem',
                      opacity: s.checked ? 1 : 0.55,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
                        <input
                          type="checkbox"
                          checked={s.checked}
                          onChange={(e) => setSections((prev) => prev.map((x) => (x.id === s.id ? { ...x, checked: e.target.checked } : x)))}
                          style={{ accentColor: 'var(--color-primary)', width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
                        />
                        <input
                          value={s.header}
                          onChange={(e) => setSections((prev) => prev.map((x) => (x.id === s.id ? { ...x, header: e.target.value } : x)))}
                          placeholder={t('noteTemplates.sectionHeaderPlaceholder', 'Section header...')}
                          style={{ ...inputStyle, fontWeight: 600 }}
                        />
                      </div>
                      <textarea
                        value={s.content}
                        onChange={(e) => setSections((prev) => prev.map((x) => (x.id === s.id ? { ...x, content: e.target.value } : x)))}
                        placeholder={t('noteTemplates.sectionContentPlaceholder', 'Section content...')}
                        rows={2}
                        style={{ ...inputStyle, resize: 'vertical' }}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <style>{`
        @keyframes sheetSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes sheetFadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
      <div style={sheetStyle}>
        {/* Handle bar on mobile */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
            <div style={{ width: '36px', height: '4px', borderRadius: '2px', backgroundColor: 'var(--color-border)' }} />
          </div>
        )}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-primary, #1A1F36)' }}>
            <FileText size={18} style={{ flexShrink: 0, width: 18, height: 18 }} />
            {t('workerDashboard.addNote', 'Add Note')}
          </h3>
          <button onClick={onClose} style={{
            width: '32px', height: '32px', borderRadius: '8px', border: 'none',
            backgroundColor: 'var(--color-surface-alt, #F1F4F8)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--color-text-secondary, #3C4257)',
          }}>
            <X size={16} style={{ flexShrink: 0, width: 16, height: 16 }} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: '0.25rem', padding: '0.25rem', borderRadius: '10px',
          backgroundColor: 'var(--color-surface-alt, #F1F4F8)', marginBottom: '0.75rem',
        }}>
          <button onClick={() => setTab('free')} style={tabButtonStyle(tab === 'free')}>
            <FileText size={14} style={{ flexShrink: 0, width: 14, height: 14 }} />
            {t('noteTemplates.freeTab', 'Free note')}
          </button>
          <button onClick={() => setTab('template')} style={tabButtonStyle(tab === 'template')}>
            <LayoutList size={14} style={{ flexShrink: 0, width: 14, height: 14 }} />
            {t('noteTemplates.templateTab', 'Template')}
          </button>
        </div>

        {/* Select meeting (if not pre-selected) */}
        {!preSelectedMeeting && meetings.length > 0 && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: '0.375rem' }}>
              {t('workerDashboard.selectMeeting', 'Select a meeting')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', maxHeight: '220px', overflow: 'auto' }}>
              {meetings.map((m, i) => {
                const todayStr = new Date().toDateString();
                const dStr = m.date ? new Date(m.date).toDateString() : '';
                const isToday = dStr === todayStr;
                const dateLabel = !isToday && m.date
                  ? new Date(m.date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
                  : null;
                const personLabel = m.studentName || m.clientName || m.treatmentType || m.type || 'Meeting';
                return (
                  <div key={m.id || i}
                    onClick={() => { setSelected(m); setNote(m.content || ''); }}
                    style={{
                      padding: '0.5rem 0.625rem', borderRadius: '8px', cursor: 'pointer',
                      border: `1.5px solid ${selected?.id === m.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                      backgroundColor: selected?.id === m.id ? 'rgba(53,87,96,0.05)' : 'transparent',
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                    }}>
                    <Clock size={13} color="var(--color-text-tertiary)" />
                    <span style={{ fontWeight: 600, fontSize: '0.8125rem', minWidth: 46 }}>{m.time || '—'}</span>
                    {dateLabel && (
                      <span style={{
                        fontSize: '0.6875rem', color: 'var(--color-text-tertiary)',
                        padding: '0.125rem 0.375rem', borderRadius: 999,
                        background: 'var(--color-surface-alt, #F1F4F8)',
                      }}>
                        {dateLabel}
                      </span>
                    )}
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-primary)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {personLabel}
                    </span>
                    {selected?.id === m.id && <Check size={14} color="var(--color-primary)" style={{ marginInlineStart: 'auto', flexShrink: 0 }} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Free note textarea */}
        {tab === 'free' && (
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('workerDashboard.enterNote', 'Enter your note...')}
            rows={4}
            style={{
              width: '100%', padding: '0.75rem', borderRadius: '8px',
              border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)',
              fontSize: '0.875rem', resize: 'vertical', fontFamily: 'inherit',
              color: 'var(--color-text-primary)',
              direction: isRTL ? 'rtl' : 'ltr',
              boxSizing: 'border-box',
            }}
          />
        )}

        {/* Template tab */}
        {tab === 'template' && renderTemplateTab()}

        {/* Save / WhatsApp buttons */}
        {!editorOpen && (
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                flex: 1, padding: '0.75rem', borderRadius: '10px', border: 'none',
                backgroundColor: canSave ? 'var(--color-primary)' : 'var(--color-border)',
                color: canSave ? 'var(--color-on-primary, #fff)' : 'white', fontWeight: 600, fontSize: '0.875rem',
                cursor: canSave ? 'pointer' : 'default',
                minHeight: '48px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
              }}>
              {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
            </button>
            {typeof onSendWhatsApp === 'function' && (
              <button
                onClick={() => canSendWhatsApp && onSendWhatsApp(effectiveText)}
                disabled={!canSendWhatsApp}
                style={{
                  flex: 1, padding: '0.75rem', borderRadius: '10px', border: 'none',
                  backgroundColor: canSendWhatsApp ? '#25D366' : 'var(--color-border)',
                  color: 'white', fontWeight: 600, fontSize: '0.875rem',
                  cursor: canSendWhatsApp ? 'pointer' : 'default',
                  minHeight: '48px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
                }}>
                <Send size={15} style={{ flexShrink: 0, width: 15, height: 15 }} />
                {t('noteTemplates.sendWhatsApp', 'Send via WhatsApp')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
